import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, BedDouble, Wallet, CalendarCheck, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const KPI = ({ icon: Icon, label, value, delta, unit = "" }) => {
  const positive = delta != null && delta >= 0;
  return (
    <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-5">
      <div className="flex items-center gap-2 text-[#5C6773]">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}</span>
      </div>
      <p className="font-display text-3xl text-[#0D253A] mt-2">{value}{unit && <span className="text-lg ml-1">{unit}</span>}</p>
      {delta != null && (
        <p className={`text-xs mt-1 flex items-center gap-1 ${positive ? "text-emerald-700" : "text-rose-700"}`}>
          {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {positive ? "+" : ""}{delta}% vs anno precedente
        </p>
      )}
    </div>
  );
};

const AdminStats = ({ authHeaders }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: s } = await axios.get(`${API}/admin/stats`, {
        headers: authHeaders, params: { year },
      });
      setData(s);
    } catch { toast.error("Impossibile caricare le statistiche"); }
    finally { setLoading(false); }
  }, [authHeaders, year]);

  useEffect(() => { load(); }, [load]);

  const maxRevenue = useMemo(() => {
    if (!data) return 0;
    const arr = [...data.current.months, ...data.previous.months].map((m) => m.revenue);
    return Math.max(1, ...arr);
  }, [data]);

  return (
    <div data-testid="studio-stats" className="space-y-6">
      {/* Year navigator */}
      <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-5 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6773] font-semibold">Anno</p>
          <p className="font-display text-3xl text-[#0D253A] mt-1">{year}</p>
          <p className="text-xs text-[#5C6773]">confrontato con {year - 1}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="stats-prev-year" onClick={() => setYear(year - 1)}
            className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51]">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="stats-next-year" onClick={() => setYear(year + 1)}
            disabled={year >= new Date().getFullYear() + 1}
            className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51] disabled:opacity-30">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading || !data ? (
        <p className="text-sm text-[#5C6773] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Carico…
        </p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI icon={Wallet} label="Ricavo lordo" value={`€ ${data.current.total_revenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`}
              delta={data.deltas.revenue_pct} />
            <KPI icon={BedDouble} label="Notti prenotate" value={data.current.total_nights} delta={data.deltas.nights_pct} />
            <KPI icon={CalendarCheck} label="Prenotazioni" value={data.current.total_bookings} delta={data.deltas.bookings_pct} />
            <KPI icon={TrendingUp} label="Occupazione" value={data.current.occupancy_pct} delta={null} unit="%" />
          </div>

          {/* Monthly comparison chart */}
          <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-[#0D253A]">Ricavo mensile — {year} vs {year - 1}</h3>
              <div className="flex items-center gap-4 text-xs text-[#5C6773]">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#C86D51]" /> {year}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-[#0D253A]/25" /> {year - 1}
                </span>
              </div>
            </div>
            <div data-testid="stats-monthly-chart" className="grid grid-cols-12 gap-1 h-52 items-end">
              {data.current.months.map((cm, i) => {
                const pm = data.previous.months[i];
                const cH = Math.max(2, (cm.revenue / maxRevenue) * 180);
                const pH = Math.max(2, (pm.revenue / maxRevenue) * 180);
                return (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <div className="w-full flex items-end justify-center gap-[2px] h-[200px]">
                      <div
                        data-testid={`stats-bar-current-${i + 1}`}
                        title={`${MONTHS_IT[i]} ${year}: € ${cm.revenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })} · ${cm.nights} notti`}
                        style={{ height: `${cH}px` }}
                        className="w-3 rounded-t bg-[#C86D51] transition-colors hover:brightness-110"
                      />
                      <div
                        data-testid={`stats-bar-previous-${i + 1}`}
                        title={`${MONTHS_IT[i]} ${year - 1}: € ${pm.revenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })} · ${pm.nights} notti`}
                        style={{ height: `${pH}px` }}
                        className="w-3 rounded-t bg-[#0D253A]/25 transition-colors hover:brightness-90"
                      />
                    </div>
                    <span className="text-[10px] text-[#5C6773]">{MONTHS_IT[i]}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monthly detail table */}
          <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-5 overflow-x-auto">
            <h3 className="font-display text-lg text-[#0D253A] mb-3">Dettaglio mensile</h3>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-[#5C6773] border-b border-[#0D253A]/10">
                <tr>
                  <th className="text-left py-2">Mese</th>
                  <th className="text-right py-2">Prenotazioni {year}</th>
                  <th className="text-right py-2">Notti {year}</th>
                  <th className="text-right py-2">Ricavo {year}</th>
                  <th className="text-right py-2">Ricavo {year - 1}</th>
                  <th className="text-right py-2">Δ</th>
                </tr>
              </thead>
              <tbody>
                {data.current.months.map((m, i) => {
                  const p = data.previous.months[i];
                  const delta = p.revenue > 0 ? ((m.revenue - p.revenue) / p.revenue) * 100 : null;
                  return (
                    <tr key={i} data-testid={`stats-row-${i + 1}`} className="border-b border-[#0D253A]/5">
                      <td className="py-2 text-[#0D253A] font-medium">{MONTHS_IT[i]}</td>
                      <td className="py-2 text-right">{m.bookings || "—"}</td>
                      <td className="py-2 text-right">{m.nights || "—"}</td>
                      <td className="py-2 text-right text-[#C86D51] font-semibold">
                        {m.revenue ? `€ ${m.revenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })}` : "—"}
                      </td>
                      <td className="py-2 text-right text-[#5C6773]">
                        {p.revenue ? `€ ${p.revenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })}` : "—"}
                      </td>
                      <td className={`py-2 text-right text-xs ${delta == null ? "text-[#5C6773]/60" : delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {delta == null ? "—" : `${delta >= 0 ? "+" : ""}${delta.toFixed(0)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-[#5C6773] leading-relaxed">
            Fonte: prenotazioni pagate (Stripe/PayPal/bonifico confermati) con data di check-in nell'anno selezionato.
            L'occupazione è calcolata sul totale notti prenotate / 365 giorni.
            {data.current.avg_night > 0 && ` · Prezzo medio/notte ${year}: € ${data.current.avg_night.toFixed(2)}.`}
          </p>
        </>
      )}
    </div>
  );
};

export default AdminStats;
